const { defer } = window.BAO.Utils

/**
 * @typedef Country
 *
 * @property {string} country_name
 * @property {string} country_handle
 * @property {string} store_handle
 */

/**
 * @typedef UserLocation
 *
 * @property {{
 *  country: {
 *    handle: string;
 *    name: string;
 *  };
 *  country_name: string;
 * }} detected_values
 */

/**
 * @typedef Store
 *
 * @property {string} country_iso_code
 * @property {string} currency_code
 * @property {boolean} current_store
 * @property {string} flag
 * @property {string} title
 * @property {string} [url]
 */

export class StoreSelectorController {
  static #resolvable = defer()

  static ready = () => this.#resolvable.promise

  /** @type {Store[]} */
  static stores = []

  static async getRecommendedStore () {
    this.setPreferredStoreFromUrl()

    if (this.preferredStore === this.currentStore.country_iso_code) {
      return this.currentStore
    }

    const recommendedStore = await this.getRecommendedStoreForUsersCountry()

    if (
      recommendedStore.country_iso_code === this.currentStore.country_iso_code
    ) {
      return this.currentStore
    }

    return recommendedStore
  }

  static setPreferredStoreFromUrl () {
    const store = new URL(window.location.toString()).searchParams.get('store')

    if (store) {
      this.preferredStore = store
    }
  }

  /** @returns {Promise<UserLocation>} */
  static async getLocationData () {
    return fetch('/browsing_context_suggestions.json').then((response) =>
      response.json(),
    )
  }

  static async getRecommendedStoreForUsersCountry () {
    const location = await this.getLocationData()

    const usersCountry = this.countries.find(
      (country) =>
        country.country_handle === location.detected_values.country.handle,
    )

    if (!usersCountry) {
      return this.currentStore
    }

    const recommendedStore = this.countries.find(
      (country) => country.store_handle === usersCountry.store_handle,
    )

    if (!recommendedStore) {
      return this.currentStore
    }

    return this.stores.find(
      (store) => store.country_iso_code === recommendedStore.country_handle,
    )
  }

  /** @param {Store[]} stores */
  static registerStores (stores) {
    this.stores.push(...stores)

    return this
  }

  static init () {
    this.#resolvable.resolve()
  }

  /** @returns {Country[]} */
  static get countries () {
    return JSON.parse(
      document.querySelector('[data-store-selector-external-el="countries"]')
        .textContent,
    ).countries
  }

  static get currentStore () {
    return this.stores.find((store) => store.current_store)
  }

  static get preferredStore () {
    return window.localStorage.getItem('store-selector:store')
  }

  static set preferredStore (preferredStore) {
    window.localStorage.setItem('store-selector:store', preferredStore)
  }
}

window.BAO.StoreSelector = StoreSelectorController

class StoreSelector extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['form', 'select']
  }

  static get requiredAttributes () {
    return ['data-design-mode']
  }

  setupListeners () {
    super.setupListeners()

    this.updateFormAction = this.updateFormAction.bind(this)

    this.listeners.add(
      this.els.select.element,
      'change',
      this.updateFormAction,
    )

    this.listeners.add(
      document.body,
      'site-modal:close',
      this.handleDismiss.bind(this),
    )

    this.handleControllerReady()
  }

  async handleControllerReady () {
    await StoreSelectorController.ready()

    const recommendedStore =
      await StoreSelectorController.getRecommendedStore()
    const originalValue = this.els.select.element.value

    this.updateSelect(recommendedStore.country_iso_code)

    if (
      this.dataset?.designMode === 'true' ||
      recommendedStore.country_iso_code === originalValue
    ) {
      return
    }

    this.open()
  }

  /** @param {CustomEvent<{ key: string }>} event */
  handleDismiss (event) {
    const isCurrentDrawer = event.detail.key === 'store-selector'

    if (!isCurrentDrawer) {
      return
    }

    StoreSelectorController.preferredStore =
      StoreSelectorController.currentStore.country_iso_code
  }

  open () {
    window.drawers.activeDrawerKey = 'store-selector'
  }

  updateFormAction () {
    const { form, select } = this.els

    const store = this.controller.stores.find(
      (store) => store.country_iso_code === select.element.value,
    )

    const url = new URL(
      store.current_store ? window.location.origin : store.url,
    )

    form.element.action = url.toString()
  }

  /** @param {string} value */
  updateSelect (value) {
    const select = this.els.select.element
    select.value = value
    /** @note Change event must be fired manually to ensure change handlers run */
    select.dispatchEvent(new Event('change'))
  }

  get controller () {
    return window.BAO.StoreSelector
  }
}

if (!window.customElements.get('store-selector')) {
  window.customElements.define('store-selector', StoreSelector)
}
